/**
 * React Hooks for User Data Management
 *
 * Provides hooks for managing user favorites, history, configurations, notes, and settings.
 */

'use client';

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  UserFavorites,
  FavoriteTechnology,
  UserHistory,
  ComparisonHistory,
  RecommendationHistory,
  SearchHistory,
  UserConfigurations,
  SavedConfiguration,
  UserNotes,
  TechnologyNote,
  DecisionNote,
  UserSettings,
  UserActivity,
  DashboardStats,
  FilterOptions,
} from '../user-data-types';
import {
  loadFavorites,
  saveFavorites,
  loadHistory,
  saveHistory,
  loadConfigurations,
  saveConfigurations,
  loadNotes,
  saveNotes,
  loadSettings,
  saveSettings,
  addActivity,
  loadActivities,
} from '../user-data-storage';

// ============================================================================
// Favorites Hook
// ============================================================================

export function useFavorites() {
  const [favorites, setFavorites] = useState<UserFavorites>(() => loadFavorites());

  const addFavorite = useCallback((techId: string, data?: Partial<FavoriteTechnology>) => {
    setFavorites((prev) => {
      const updated: UserFavorites = {
        technologies: [
          ...prev.technologies.filter((f) => f.technologyId !== techId),
          {
            technologyId: techId,
            addedAt: new Date().toISOString(),
            ...data,
          },
        ],
        lastUpdated: new Date().toISOString(),
      };
      saveFavorites(updated);
      addActivity({
        type: 'favorite',
        description: `Added technology to favorites`,
        relatedId: techId,
      });
      return updated;
    });
  }, []);

  const removeFavorite = useCallback((techId: string) => {
    setFavorites((prev) => {
      const updated: UserFavorites = {
        technologies: prev.technologies.filter((f) => f.technologyId !== techId),
        lastUpdated: new Date().toISOString(),
      };
      saveFavorites(updated);
      addActivity({
        type: 'unfavorite',
        description: `Removed technology from favorites`,
        relatedId: techId,
      });
      return updated;
    });
  }, []);

  const updateFavorite = useCallback(
    (techId: string, updates: Partial<FavoriteTechnology>) => {
      setFavorites((prev) => {
        const updated: UserFavorites = {
          technologies: prev.technologies.map((f) =>
            f.technologyId === techId ? { ...f, ...updates } : f
          ),
          lastUpdated: new Date().toISOString(),
        };
        saveFavorites(updated);
        return updated;
      });
    },
    []
  );

  const isFavorite = useCallback(
    (techId: string) => {
      return favorites.technologies.some((f) => f.technologyId === techId);
    },
    [favorites]
  );

  const getFavorite = useCallback(
    (techId: string) => {
      return favorites.technologies.find((f) => f.technologyId === techId);
    },
    [favorites]
  );

  const getFavoritesByTag = useCallback(
    (tag: string) => {
      return favorites.technologies.filter((f) => f.tags?.includes(tag));
    },
    [favorites]
  );

  return {
    favorites,
    addFavorite,
    removeFavorite,
    updateFavorite,
    isFavorite,
    getFavorite,
    getFavoritesByTag,
  };
}

// ============================================================================
// History Hook
// ============================================================================

export function useHistory() {
  const [history, setHistory] = useState<UserHistory>(() => loadHistory());

  const addComparison = useCallback((comparison: Omit<ComparisonHistory, 'id' | 'createdAt'>) => {
    setHistory((prev) => {
      const newComparison: ComparisonHistory = {
        ...comparison,
        id: generateId(),
        createdAt: new Date().toISOString(),
      };
      const updated: UserHistory = {
        ...prev,
        comparisons: [newComparison, ...prev.comparisons],
        lastUpdated: new Date().toISOString(),
      };
      saveHistory(updated);
      addActivity({
        type: 'comparison',
        description: `Created comparison: ${comparison.name || 'Untitled'}`,
        relatedId: newComparison.id,
      });
      return updated;
    });
  }, []);

  const updateComparison = useCallback(
    (id: string, updates: Partial<ComparisonHistory>) => {
      setHistory((prev) => {
        const updated: UserHistory = {
          ...prev,
          comparisons: prev.comparisons.map((c) => (c.id === id ? { ...c, ...updates } : c)),
          lastUpdated: new Date().toISOString(),
        };
        saveHistory(updated);
        return updated;
      });
    },
    []
  );

  const deleteComparison = useCallback((id: string) => {
    setHistory((prev) => {
      const updated: UserHistory = {
        ...prev,
        comparisons: prev.comparisons.filter((c) => c.id !== id),
        lastUpdated: new Date().toISOString(),
      };
      saveHistory(updated);
      return updated;
    });
  }, []);

  const addRecommendation = useCallback(
    (recommendation: Omit<RecommendationHistory, 'id' | 'createdAt'>) => {
      setHistory((prev) => {
        const newRecommendation: RecommendationHistory = {
          ...recommendation,
          id: generateId(),
          createdAt: new Date().toISOString(),
        };
        const updated: UserHistory = {
          ...prev,
          recommendations: [newRecommendation, ...prev.recommendations],
          lastUpdated: new Date().toISOString(),
        };
        saveHistory(updated);
        addActivity({
          type: 'recommendation',
          description: `Saved recommendation: ${recommendation.name || 'Untitled'}`,
          relatedId: newRecommendation.id,
        });
        return updated;
      });
    },
    []
  );

  const deleteRecommendation = useCallback((id: string) => {
    setHistory((prev) => {
      const updated: UserHistory = {
        ...prev,
        recommendations: prev.recommendations.filter((r) => r.id !== id),
        lastUpdated: new Date().toISOString(),
      };
      saveHistory(updated);
      return updated;
    });
  }, []);

  const addSearch = useCallback((query: string, category?: string) => {
    setHistory((prev) => {
      const newSearch: SearchHistory = {
        id: generateId(),
        query,
        category,
        timestamp: new Date().toISOString(),
      };
      const updated: UserHistory = {
        ...prev,
        searches: [newSearch, ...prev.searches.slice(0, 49)], // Keep last 50 searches
        lastUpdated: new Date().toISOString(),
      };
      saveHistory(updated);
      return updated;
    });
  }, []);

  const clearSearchHistory = useCallback(() => {
    setHistory((prev) => {
      const updated: UserHistory = {
        ...prev,
        searches: [],
        lastUpdated: new Date().toISOString(),
      };
      saveHistory(updated);
      return updated;
    });
  }, []);

  return {
    history,
    addComparison,
    updateComparison,
    deleteComparison,
    addRecommendation,
    deleteRecommendation,
    addSearch,
    clearSearchHistory,
  };
}

// ============================================================================
// Configurations Hook
// ============================================================================

export function useConfigurations() {
  const [configurations, setConfigurations] = useState<UserConfigurations>(() =>
    loadConfigurations()
  );

  const saveConfiguration = useCallback(
    (config: Omit<SavedConfiguration, 'id' | 'createdAt' | 'lastUsed'>) => {
      setConfigurations((prev) => {
        const newConfig: SavedConfiguration = {
          ...config,
          id: generateId(),
          createdAt: new Date().toISOString(),
          lastUsed: new Date().toISOString(),
        };

        const targetArray = config.isTemplate ? 'templates' : 'saved';
        const updated: UserConfigurations = {
          ...prev,
          [targetArray]: [newConfig, ...prev[targetArray]],
          lastUpdated: new Date().toISOString(),
        };
        saveConfigurations(updated);
        addActivity({
          type: 'configuration',
          description: `Saved configuration: ${config.name}`,
          relatedId: newConfig.id,
        });
        return updated;
      });
    },
    []
  );

  const updateConfiguration = useCallback(
    (id: string, updates: Partial<SavedConfiguration>) => {
      setConfigurations((prev) => {
        const updated: UserConfigurations = {
          saved: prev.saved.map((c) => (c.id === id ? { ...c, ...updates } : c)),
          templates: prev.templates.map((c) => (c.id === id ? { ...c, ...updates } : c)),
          lastUpdated: new Date().toISOString(),
        };
        saveConfigurations(updated);
        return updated;
      });
    },
    []
  );

  const deleteConfiguration = useCallback((id: string) => {
    setConfigurations((prev) => {
      const updated: UserConfigurations = {
        saved: prev.saved.filter((c) => c.id !== id),
        templates: prev.templates.filter((c) => c.id !== id),
        lastUpdated: new Date().toISOString(),
      };
      saveConfigurations(updated);
      return updated;
    });
  }, []);

  const markAsUsed = useCallback((id: string) => {
    setConfigurations((prev) => {
      const updated: UserConfigurations = {
        saved: prev.saved.map((c) =>
          c.id === id ? { ...c, lastUsed: new Date().toISOString() } : c
        ),
        templates: prev.templates.map((c) =>
          c.id === id ? { ...c, lastUsed: new Date().toISOString() } : c
        ),
        lastUpdated: new Date().toISOString(),
      };
      saveConfigurations(updated);
      return updated;
    });
  }, []);

  const getConfiguration = useCallback(
    (id: string) => {
      return (
        configurations.saved.find((c) => c.id === id) ||
        configurations.templates.find((c) => c.id === id)
      );
    },
    [configurations]
  );

  return {
    configurations,
    saveConfiguration,
    updateConfiguration,
    deleteConfiguration,
    markAsUsed,
    getConfiguration,
  };
}

// ============================================================================
// Notes Hook
// ============================================================================

export function useNotes() {
  const [notes, setNotes] = useState<UserNotes>(() => loadNotes());

  const addTechnologyNote = useCallback(
    (note: Omit<TechnologyNote, 'id' | 'createdAt' | 'updatedAt'>) => {
      setNotes((prev) => {
        const newNote: TechnologyNote = {
          ...note,
          id: generateId(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const updated: UserNotes = {
          ...prev,
          technologyNotes: [newNote, ...prev.technologyNotes],
          lastUpdated: new Date().toISOString(),
        };
        saveNotes(updated);
        addActivity({
          type: 'note',
          description: `Created note: ${note.title}`,
          relatedId: newNote.id,
        });
        return updated;
      });
    },
    []
  );

  const updateTechnologyNote = useCallback((id: string, updates: Partial<TechnologyNote>) => {
    setNotes((prev) => {
      const updated: UserNotes = {
        ...prev,
        technologyNotes: prev.technologyNotes.map((n) =>
          n.id === id ? { ...n, ...updates, updatedAt: new Date().toISOString() } : n
        ),
        lastUpdated: new Date().toISOString(),
      };
      saveNotes(updated);
      return updated;
    });
  }, []);

  const deleteTechnologyNote = useCallback((id: string) => {
    setNotes((prev) => {
      const updated: UserNotes = {
        ...prev,
        technologyNotes: prev.technologyNotes.filter((n) => n.id !== id),
        lastUpdated: new Date().toISOString(),
      };
      saveNotes(updated);
      return updated;
    });
  }, []);

  const addDecisionNote = useCallback(
    (note: Omit<DecisionNote, 'id' | 'createdAt' | 'updatedAt'>) => {
      setNotes((prev) => {
        const newNote: DecisionNote = {
          ...note,
          id: generateId(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const updated: UserNotes = {
          ...prev,
          decisionNotes: [newNote, ...prev.decisionNotes],
          lastUpdated: new Date().toISOString(),
        };
        saveNotes(updated);
        addActivity({
          type: 'note',
          description: `Created decision note: ${note.title}`,
          relatedId: newNote.id,
        });
        return updated;
      });
    },
    []
  );

  const updateDecisionNote = useCallback((id: string, updates: Partial<DecisionNote>) => {
    setNotes((prev) => {
      const updated: UserNotes = {
        ...prev,
        decisionNotes: prev.decisionNotes.map((n) =>
          n.id === id ? { ...n, ...updates, updatedAt: new Date().toISOString() } : n
        ),
        lastUpdated: new Date().toISOString(),
      };
      saveNotes(updated);
      return updated;
    });
  }, []);

  const deleteDecisionNote = useCallback((id: string) => {
    setNotes((prev) => {
      const updated: UserNotes = {
        ...prev,
        decisionNotes: prev.decisionNotes.filter((n) => n.id !== id),
        lastUpdated: new Date().toISOString(),
      };
      saveNotes(updated);
      return updated;
    });
  }, []);

  const getNotesByTechnology = useCallback(
    (technologyId: string) => {
      return notes.technologyNotes.filter((n) => n.technologyId === technologyId);
    },
    [notes]
  );

  const searchNotes = useCallback(
    (query: string) => {
      const lowerQuery = query.toLowerCase();
      return {
        technologyNotes: notes.technologyNotes.filter(
          (n) =>
            n.title.toLowerCase().includes(lowerQuery) ||
            n.content.toLowerCase().includes(lowerQuery)
        ),
        decisionNotes: notes.decisionNotes.filter(
          (n) =>
            n.title.toLowerCase().includes(lowerQuery) ||
            n.content.toLowerCase().includes(lowerQuery)
        ),
      };
    },
    [notes]
  );

  return {
    notes,
    addTechnologyNote,
    updateTechnologyNote,
    deleteTechnologyNote,
    addDecisionNote,
    updateDecisionNote,
    deleteDecisionNote,
    getNotesByTechnology,
    searchNotes,
  };
}

// ============================================================================
// Settings Hook
// ============================================================================

export function useSettings() {
  const [settings, setSettings] = useState<UserSettings>(() => loadSettings());

  const updateSettings = useCallback((updates: Partial<UserSettings>) => {
    setSettings((prev) => {
      const updated: UserSettings = {
        ...prev,
        ...updates,
        lastUpdated: new Date().toISOString(),
      };
      saveSettings(updated);
      return updated;
    });
  }, []);

  const updateDisplayPreferences = useCallback((updates: Partial<UserSettings['displayPreferences']>) => {
    setSettings((prev) => {
      const updated: UserSettings = {
        ...prev,
        displayPreferences: {
          ...prev.displayPreferences,
          ...updates,
        },
        lastUpdated: new Date().toISOString(),
      };
      saveSettings(updated);
      return updated;
    });
  }, []);

  const resetSettings = useCallback(() => {
    const defaultSettings = loadSettings();
    setSettings(defaultSettings);
    saveSettings(defaultSettings);
  }, []);

  return {
    settings,
    updateSettings,
    updateDisplayPreferences,
    resetSettings,
  };
}

// ============================================================================
// Dashboard Stats Hook
// ============================================================================

export function useDashboardStats(): DashboardStats {
  const { favorites } = useFavorites();
  const { configurations } = useConfigurations();
  const { history } = useHistory();
  const { notes } = useNotes();

  const activities = loadActivities();

  const stats = useMemo<DashboardStats>(() => {
    // Calculate top categories from favorites
    const categoryCount = new Map<string, number>();
    favorites.technologies.forEach((fav) => {
      // We'd need to look up the category from the tech database
      // For now, just count as generic
    });

    const topCategories = Array.from(categoryCount.entries())
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Calculate experience by category from favorites
    const experienceByCategory = favorites.technologies
      .filter((f) => f.experience)
      .map((f) => ({
        category: f.technologyId, // Would be better to look up actual category
        level: f.experience || 'none',
      }));

    return {
      totalFavorites: favorites.technologies.length,
      totalConfigurations: configurations.saved.length + configurations.templates.length,
      totalComparisons: history.comparisons.length,
      totalNotes: notes.technologyNotes.length + notes.decisionNotes.length,
      recentActivity: activities.activities.slice(-10).reverse(),
      topCategories,
      experienceByCategory,
    };
  }, [favorites, configurations, history, notes, activities]);

  return stats;
}

// ============================================================================
// Helper Functions
// ============================================================================

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
